/**
 * Workspace product detection and copy/config for notifications + support.
 */

import { isFundGeniusAppLoginHost } from '../utils/fundgeniusLoginBranding';
import {
  buildAgencyFundGeniusPath,
  buildPlatformFundGeniusPath,
  buildStandaloneFundGeniusPath,
} from '../utils/funding/routes';

export const WORKSPACE_PRODUCTS = {
  SELF_REPAIR: 'self_repair',
  FUNDGENIUS_HQ: 'fundgenius_hq',
  AGENCY: 'agency',
};

export function isFundGeniusHqPath(pathname = '') {
  const path = String(pathname || '');
  return (
    path.startsWith('/fundgenius-hq')
    || path.startsWith('/platform/fundgenius-hq')
    || /\/fundgenius-hq(\/|$)/.test(path)
  );
}

export function isAgencyWorkspacePath(pathname = '') {
  const path = String(pathname || '');
  return /^\/agency\/[^/]+/.test(path) && !isFundGeniusHqPath(path);
}

export function detectWorkspaceProduct({
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
} = {}) {
  if (isFundGeniusAppLoginHost(hostname) || isFundGeniusHqPath(pathname)) {
    return WORKSPACE_PRODUCTS.FUNDGENIUS_HQ;
  }
  if (isAgencyWorkspacePath(pathname)) {
    return WORKSPACE_PRODUCTS.AGENCY;
  }
  return WORKSPACE_PRODUCTS.SELF_REPAIR;
}

export function resolveFundGeniusBasePath(pathname = '') {
  const path = String(pathname || '');
  if (path.startsWith('/platform/fundgenius-hq')) {
    return buildPlatformFundGeniusPath;
  }
  const agencyMatch = path.match(/^\/agency\/([^/]+)\/fundgenius-hq/);
  if (agencyMatch) {
    return (segment = '') => buildAgencyFundGeniusPath(agencyMatch[1], segment);
  }
  return buildStandaloneFundGeniusPath;
}

export const PRODUCT_NOTIFICATION_CONFIG = {
  [WORKSPACE_PRODUCTS.SELF_REPAIR]: {
    useI18n: true,
    hasNotificationsRoute: true,
    viewAllPath: '/settings/notifications',
    viewAllComingSoon: null,
  },
  [WORKSPACE_PRODUCTS.AGENCY]: {
    useI18n: false,
    hasNotificationsRoute: true,
    viewAllPath: null,
    viewAllComingSoon: null,
    // Empty until agency notifications API is wired — never show self-repair samples.
    notifications: [],
    tabs: [
      { id: 'inbox', label: 'Inbox' },
      { id: 'billing', label: 'Billing' },
      { id: 'updates', label: 'Updates' },
    ],
  },
  [WORKSPACE_PRODUCTS.FUNDGENIUS_HQ]: {
    useI18n: false,
    hasNotificationsRoute: false,
    viewAllPath: null,
    viewAllComingSoon: 'Notifications center coming soon.',
    notifications: [
      {
        id: 'fg-inbox-1',
        tab: 'inbox',
        title: 'New funding client added',
        body: 'A new client profile is ready for intake review.',
        time: '2 hours ago',
        unread: true,
      },
      {
        id: 'fg-inbox-2',
        tab: 'inbox',
        title: 'Application needs follow-up',
        body: 'One lender application is missing a decision update.',
        time: '5 hours ago',
        unread: true,
      },
      {
        id: 'fg-inbox-3',
        tab: 'inbox',
        title: 'Document review needed',
        body: 'A client uploaded files that may need classification.',
        time: '1 day ago',
        unread: true,
      },
      {
        id: 'fg-inbox-4',
        tab: 'inbox',
        title: 'Readiness score changed',
        body: 'A client’s funding readiness changed after profile updates.',
        time: '1 day ago',
        unread: false,
      },
      {
        id: 'fg-offers-1',
        tab: 'offers',
        title: 'Funding OS plan upgrade',
        body: 'Unlock AI Advisor, lender sequencing, and advanced portfolio insights.',
        time: '1 day ago',
        unread: true,
      },
      {
        id: 'fg-offers-2',
        tab: 'offers',
        title: 'Add intake portal',
        body: 'Let clients submit documents and funding details from your branded intake link.',
        time: '2 days ago',
        unread: false,
      },
      {
        id: 'fg-updates-1',
        tab: 'updates',
        title: 'Lender matching updated',
        body: 'Bank Match now prioritizes bureau pull and product fit.',
        time: '1 day ago',
        unread: false,
      },
      {
        id: 'fg-updates-2',
        tab: 'updates',
        title: 'Pipeline reporting improved',
        body: 'Approval totals and submission status now refresh faster.',
        time: '3 days ago',
        unread: false,
      },
      {
        id: 'fg-updates-3',
        tab: 'updates',
        title: 'AI Advisor update',
        body: 'New recommendations can explain approval and denial patterns.',
        time: '4 days ago',
        unread: false,
      },
    ],
  },
};

const SELF_REPAIR_SUPPORT_CATEGORIES = [
  { key: 'technical', labelKey: 'technical', value: 'technical_support' },
  { key: 'billing', labelKey: 'billing', value: 'billing_invoice' },
  { key: 'agencySubscription', labelKey: 'agencySubscription', value: 'agency_subscription' },
  { key: 'affiliate', labelKey: 'affiliate', value: 'affiliate' },
  { key: 'existingTicket', labelKey: 'existingTicket', value: 'existing_ticket' },
  { key: 'creditReportImport', labelKey: 'creditReportImport', value: 'credit_report_import' },
  { key: 'letterDisputeGeneration', labelKey: 'letterDisputeGeneration', value: 'letter_dispute_generation' },
  { key: 'documentUpload', labelKey: 'documentUpload', value: 'document_upload' },
  { key: 'accountAccess', labelKey: 'accountAccess', value: 'account_access' },
  { key: 'general', labelKey: 'general', value: 'general_question' },
];

const FUNDGENIUS_SUPPORT_CATEGORIES = [
  { key: 'general', label: 'General Question', value: 'general_question' },
  { key: 'accountAccess', label: 'Account Access / Login Issue', value: 'account_access' },
  { key: 'fundingClientSetup', label: 'Funding Client Setup', value: 'funding_client_setup' },
  { key: 'documentUpload', label: 'Document Upload Issue', value: 'document_upload' },
  { key: 'applicationPipeline', label: 'Application Pipeline Issue', value: 'application_pipeline' },
  { key: 'lenderMatch', label: 'Lender Match / Bank Strategy', value: 'lender_match_strategy' },
  { key: 'readinessScore', label: 'Readiness Score Question', value: 'readiness_score' },
  { key: 'aiAdvisor', label: 'AI Advisor Issue', value: 'ai_advisor' },
  { key: 'intakePortal', label: 'Intake Portal Support', value: 'intake_portal' },
  { key: 'billing', label: 'Billing / Invoice Support', value: 'billing_invoice' },
  { key: 'agencySubscription', label: 'Agency Subscription Support', value: 'agency_subscription' },
  { key: 'existingTicket', label: 'Existing Ticket', value: 'existing_ticket' },
  { key: 'technical', label: 'Technical Support', value: 'technical_support' },
];

const AGENCY_SUPPORT_CATEGORIES = [
  { key: 'technical', label: 'Technical Support', value: 'technical_support' },
  { key: 'billing', label: 'Billing / Invoice Support', value: 'billing_invoice' },
  { key: 'agencySubscription', label: 'Agency Subscription Support', value: 'agency_subscription' },
  { key: 'staffPermissions', label: 'Staff & Permission Issue', value: 'staff_permissions' },
  { key: 'clientPortal', label: 'Client Portal Issue', value: 'client_portal' },
  { key: 'creditReportImport', label: 'Credit Report Import Issue', value: 'credit_report_import' },
  { key: 'letterGeneration', label: 'Letter / Dispute Generation Issue', value: 'letter_generation' },
  { key: 'documentUpload', label: 'Document Upload Issue', value: 'document_upload' },
  { key: 'automationWorkflow', label: 'Automation / Workflow Issue', value: 'automation_workflow' },
  { key: 'integrationIssue', label: 'Integration Issue', value: 'integration_issue' },
  { key: 'accountAccess', label: 'Account Access / Login Issue', value: 'account_access' },
  { key: 'existingTicket', label: 'Existing Ticket', value: 'existing_ticket' },
  { key: 'general', label: 'General Question', value: 'general_question' },
];

export const PRODUCT_SUPPORT_CONFIG = {
  [WORKSPACE_PRODUCTS.SELF_REPAIR]: {
    productId: 'self_repair',
    i18nPrefix: 'supportWidget',
    categories: SELF_REPAIR_SUPPORT_CATEGORIES,
    defaultCategoryKey: 'general',
    chatMode: 'credit_coach',
    includeCreditReportUpload: true,
    billingPath: '/billing',
    documentsPath: '/documents',
    creditReportPath: '/credit-report/import',
    premiumUpgradeLabel: null,
  },
  [WORKSPACE_PRODUCTS.AGENCY]: {
    productId: 'agency',
    i18nPrefix: null,
    categories: AGENCY_SUPPORT_CATEGORIES,
    defaultCategoryKey: 'general',
    chatMode: 'agency_support',
    includeCreditReportUpload: false,
    billingPath: null,
    documentsPath: null,
    creditReportPath: null,
    hideBuyCredits: true,
    strings: {
      headerTitle: 'FundElite Agency Support',
      headerSubtitle: 'Agency workspace help',
      headerDescription:
        'Ask us about clients, disputes, letters, staff permissions, billing, or integrations.',
      premiumTitle: 'Agency Priority Support',
      premiumDescription:
        'Faster help for agency operations, imports, letter generation, and workspace setup.',
      premiumUpgrade: 'Upgrade',
      premiumContactPriority: 'Contact Agency Owner',
      categoryLabel: 'Category',
      composerPlaceholder: 'Describe the agency issue…',
      typingAi: 'AI Support is writing',
      typingSupport: 'Support is writing',
      attachmentPreview: 'Attachment ready to send',
      attachmentRemove: 'Remove',
      close: 'Close support',
      cancel: 'Cancel',
      uploadTitle: 'What are you uploading?',
      uploadSubtitle: 'Choose where this file should go.',
      uploadClientDocumentTitle: 'Client Document',
      uploadClientDocumentDescription: 'Files tied to a managed client case.',
      uploadClientDocumentCta: 'Continue to Documents',
      uploadSupportAttachmentTitle: 'Support Attachment',
      uploadSupportAttachmentDescription:
        'Screenshots, bug proof, billing proof, or files related to this support request.',
      uploadSupportAttachmentCta: 'Attach to this message',
      uploadNoneTitle: 'None of the above',
      uploadNoneDescription: 'Upload this file to the current support conversation.',
      uploadNoneCta: 'Upload here',
      ticketsSelectLabel: 'Select an open ticket',
      ticketsEmpty: 'No open tickets — a new ticket will be created',
      composerSend: 'Send message',
      composerAttach: 'Attach file',
    },
    premiumUpgradeLabel: 'Upgrade',
  },
  [WORKSPACE_PRODUCTS.FUNDGENIUS_HQ]: {
    productId: 'fundgenius_hq',
    i18nPrefix: null,
    categories: FUNDGENIUS_SUPPORT_CATEGORIES,
    defaultCategoryKey: 'general',
    chatMode: 'funding_advisor',
    includeCreditReportUpload: false,
    billingPath: null,
    documentsPath: null,
    creditReportPath: null,
    strings: {
      headerTitle: 'FundGenius HQ Support',
      headerSubtitle: 'Funding workspace help',
      headerDescription:
        'Ask us about funding clients, applications, documents, lender strategy, or your workspace.',
      premiumTitle: 'Funding OS Support',
      premiumDescription:
        'Get guided setup, workflow troubleshooting, and faster help for your funding workspace.',
      premiumUpgrade: 'View Plan',
      premiumContactPriority: 'Contact Priority Support',
      categoryLabel: 'Category',
      composerPlaceholder: 'Ask about clients, applications, lenders, or documents…',
      typingAi: 'AI Support is writing',
      typingSupport: 'Support is writing',
      attachmentPreview: 'Attachment ready to send',
      attachmentRemove: 'Remove',
      close: 'Close support',
      cancel: 'Cancel',
      uploadTitle: 'What are you uploading?',
      uploadSubtitle: 'Choose where this file should go.',
      uploadClientDocumentTitle: 'Client Document',
      uploadClientDocumentDescription:
        'IDs, bank statements, tax returns, or files tied to a funding client profile.',
      uploadClientDocumentCta: 'Continue to Documents',
      uploadSupportAttachmentTitle: 'Support Attachment',
      uploadSupportAttachmentDescription:
        'Screenshots, bug proof, billing proof, or files related to this support request.',
      uploadSupportAttachmentCta: 'Attach to this message',
      uploadNoneTitle: 'None of the above',
      uploadNoneDescription: 'Upload this file to the current support conversation.',
      uploadNoneCta: 'Upload here',
      ticketsSelectLabel: 'Select an open ticket',
      ticketsEmpty: 'No open tickets — a new ticket will be created',
      composerSend: 'Send message',
      composerAttach: 'Attach file',
    },
    premiumUpgradeLabel: 'View Plan',
  },
};

export function getProductNotificationConfig(product = WORKSPACE_PRODUCTS.SELF_REPAIR) {
  return PRODUCT_NOTIFICATION_CONFIG[product] || PRODUCT_NOTIFICATION_CONFIG[WORKSPACE_PRODUCTS.SELF_REPAIR];
}

export function getProductSupportConfig(product = WORKSPACE_PRODUCTS.SELF_REPAIR) {
  return PRODUCT_SUPPORT_CONFIG[product] || PRODUCT_SUPPORT_CONFIG[WORKSPACE_PRODUCTS.SELF_REPAIR];
}

export function getFundGeniusNotificationsPath(pathname = '') {
  const pathFor = resolveFundGeniusBasePath(pathname);
  return pathFor('notifications');
}

export function getFundGeniusManagePlanPath(pathname = '') {
  const pathFor = resolveFundGeniusBasePath(pathname);
  return pathFor('settings');
}
